// Floor and board tiles (UX §6.1, research §2 "Floor and walls"): floor base / alt checker with grid lines
// and seeded speckle variants, the steel plate under built machines, the rock 16-case autotile (bit set =
// that neighbour is rock too: N 1, E 2, S 4, W 8), the locked expansion rock and the ghost blueprint.
import { drawParts, fillP, outlineUnits, part } from './passes.ts';
import { mulberry32 } from './rng.ts';
import { blobPts, box, circle, poly, rrect, smoothPoly } from './shapes.ts';
import type { Generator, TileSpec } from './types.ts';

export const ROCK_N = 1;
export const ROCK_E = 2;
export const ROCK_S = 4;
export const ROCK_W = 8;

export const tileGen: Generator<TileSpec> = (c, spec, px, env) => {
  const { pal } = env;
  const rng = mulberry32(spec.seed);
  c.save();
  c.scale(px / 100, px / 100);
  const ow = outlineUnits(env.sprites.outline, env.sprites.outlineMinPx, px, env.dpr);
  if (spec.tile === 'floor') {
    const alt = spec.index % 2 === 1;
    c.fillStyle = pal.hex(alt ? 'floor.alt' : 'floor.base');
    c.fillRect(0, 0, 100, 100);
    if (spec.index >= 2) {
      for (let i = 0; i < 14; i++) {
        const x = 6 + rng() * 88;
        const y = 6 + rng() * 88;
        const r = 0.8 + rng() * 1.6;
        fillP(c, circle(x, y, r), pal.hex(rng() < 0.6 ? 'floor.speckle' : 'floor.grid'));
      }
    }
    c.fillStyle = pal.hex('floor.grid');
    c.fillRect(0, 0, 100, 1.2);
    c.fillRect(0, 0, 1.2, 100);
  } else if (spec.tile === 'plate') {
    const s = pal.sw('plate.steel');
    fillP(c, rrect(3, 3, 94, 94, 8), s.outline);
    fillP(c, rrect(5, 5, 90, 90, 6.5), s.base);
    fillP(c, rrect(5, 5, 90, 4, 2), s.light);
    fillP(c, rrect(5, 87, 90, 8, 4), s.shade);
    for (const [x, y] of [
      [12, 12],
      [88, 12],
      [12, 86],
      [88, 86],
    ] as const) {
      fillP(c, circle(x, y, 2.6), s.shade);
      fillP(c, circle(x - 0.6, y - 0.6, 1), s.hi);
    }
  } else if (spec.tile === 'rock' || spec.tile === 'locked') {
    const mask = spec.tile === 'rock' ? spec.index : 15;
    const open = (bit: number) => (mask & bit) === 0;
    const ins = 7;
    const x0 = open(ROCK_W) ? ins : -8;
    const x1 = open(ROCK_E) ? 100 - ins : 108;
    const y0 = open(ROCK_N) ? ins : -8;
    const y1 = open(ROCK_S) ? 100 - ins : 108;
    const R = 20;
    const radii = [
      open(ROCK_N) && open(ROCK_W) ? R : 0,
      open(ROCK_N) && open(ROCK_E) ? R : 0,
      open(ROCK_S) && open(ROCK_E) ? R : 0,
      open(ROCK_S) && open(ROCK_W) ? R : 0,
    ];
    const path = rrect(x0, y0, x1 - x0, y1 - y0, radii);
    const rock = pal.sw('rock.base');
    const light = pal.sw('rock.light');
    const fleck = pal.sw('rock.fleck');
    drawParts(
      c,
      [
        part(path, rock, box(x0, y0, x1 - x0, y1 - y0), {
          outline: pal.hex('mach.outline'),
          detail: (cc) => {
            cc.save();
            cc.beginPath();
            path(cc);
            cc.clip();
            for (let i = 0; i < 5; i++) {
              const bx = 10 + rng() * 80;
              const by = 10 + rng() * 80;
              const pts = blobPts(rng, bx, by, 9 + rng() * 6, 6 + rng() * 5, 7, 0.18, rng() * 6);
              fillP(cc, smoothPoly(pts), light.base);
              fillP(cc, smoothPoly(pts.map(([x, y]) => [x, y + 2.5] as const)), light.shade);
            }
            for (let i = 0; i < 3; i++) {
              const fx = 12 + rng() * 76;
              const fy = 12 + rng() * 76;
              const s = 2.2 + rng() * 2;
              fillP(
                cc,
                poly([
                  [fx, fy - s * 1.6],
                  [fx + s, fy],
                  [fx, fy + s * 1.2],
                  [fx - s, fy],
                ]),
                fleck.base,
              );
              fillP(cc, circle(fx - s * 0.2, fy - s * 0.5, s * 0.35), fleck.hi);
            }
            if (spec.tile === 'locked') {
              const brass = pal.sw('ui.brass');
              cc.strokeStyle = light.light;
              cc.lineWidth = ow * 0.45;
              cc.lineCap = 'round';
              cc.beginPath();
              cc.moveTo(18, 30);
              cc.lineTo(34, 42);
              cc.lineTo(30, 58);
              cc.moveTo(82, 64);
              cc.lineTo(66, 58);
              cc.lineTo(70, 78);
              cc.stroke();
              // brass padlock emblem
              cc.lineWidth = 4.2;
              cc.strokeStyle = pal.hex('material.steel');
              cc.beginPath();
              cc.arc(50, 46, 8.5, Math.PI, 0);
              cc.stroke();
              fillP(cc, rrect(37, 45, 26, 21, 4), brass.outline);
              fillP(cc, rrect(39, 47, 22, 17, 3), brass.base);
              fillP(cc, circle(50, 54, 2.6), brass.outline);
              fillP(cc, rrect(49, 55, 2, 5, 1), brass.outline);
            }
            cc.restore();
          },
        }),
      ],
      { ow, rim: false, halo: null, glow: null, shadeBand: pal.rules.shadeBand, hiW: 4 },
    );
  } else {
    // ghost blueprint: translucent fill, dashed outline, corner brackets
    const sel = pal.sw('glow.select');
    fillP(c, rrect(6, 6, 88, 88, 10), pal.rgba('glow.select', 0.22));
    c.setLineDash([7, 5]);
    c.strokeStyle = sel.base;
    c.lineWidth = 2.6;
    c.beginPath();
    rrect(6, 6, 88, 88, 10)(c);
    c.stroke();
    c.setLineDash([]);
    c.lineWidth = 4;
    c.lineCap = 'round';
    for (const [x, y, dx, dy] of [
      [4, 4, 1, 1],
      [96, 4, -1, 1],
      [4, 96, 1, -1],
      [96, 96, -1, -1],
    ] as const) {
      c.beginPath();
      c.moveTo(x + dx * 14, y);
      c.lineTo(x, y);
      c.lineTo(x, y + dy * 14);
      c.stroke();
    }
  }
  c.restore();
};
